import React, { FC, useState } from 'react';
import { TextField } from '@mui/material';
import * as Sentry from '@sentry/react';
import { QueryObserverResult } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { SalesforceConfig } from '~api/admin/businesses/billing/getSalesforceId';
import { ApiError } from '~types/api';
import { Button, ButtonVariant } from 'components/common/buttons/Button';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { putSalesforceId } from 'api/admin/businesses/billing/putSalesforceId';
import styles from './BusinessConfigurationForm.module.scss';

export const BusinessConfigurationForm: FC<{
  currentSalesforceId: string | null;
  businessId: number;
  refetchId: () => Promise<QueryObserverResult<SalesforceConfig, ApiError>>;
}> = ({ currentSalesforceId, businessId, refetchId }) => {
  const [isUpdatingId, setIsUpdatingId] = useState(false);
  const [salesforceId, setSalesforceId] = useState(currentSalesforceId);

  if (isUpdatingId) {
    return <CentsLoader />;
  }

  return (
    <>
      <TextField
        label="Salesforce ID"
        onChange={(e) => setSalesforceId(e.currentTarget.value)}
        value={salesforceId}
        className={styles.input}
      />
      <Button
        onClick={async () => {
          try {
            setIsUpdatingId(true);
            await putSalesforceId({ businessId, salesforceId: salesforceId || null });
            await refetchId();
            toast.success('Salesforce id updated');
          } catch (error) {
            if (error.isAxiosError && error.response.status === 409) {
              toast.error('Salesforce id is busy');
            } else if (error.isAxiosError && error.response.status === 422) {
              toast.error(error.response?.data?.error || 'Salesforce ID must have a valid format');
            } else {
              Sentry.captureException(error);
            }
          } finally {
            setIsUpdatingId(false);
          }
        }}
        type={ButtonVariant.CONTAINED}
      >
        Save
      </Button>
    </>
  );
};
